import { useState } from "react";
import { MoveLeft, UserRound, Volleyball, Star, AlignStartVertical, } from "lucide-react";
import { useDepartments, useUser, useGetPermissions } from "../Use-auth";
import PayRollDashboard from "../ui/PayRollDashboard";
import SkeletonPage from "./SkeletonPage ";
import PermissionPopup from "./PermissionPopup";

export default function PayRollPage() {
    const { data: departments = [], isLoading: fetching } = useDepartments();
    const [employeeSearch, setEmployeeSearch] = useState("");
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
    const [selectedDepartment, setSelectedDepartment] = useState('');

    const today = new Date();
    const [selectedYear, setSelectedYear] = useState(today.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(
        (today.getMonth() + 1).toString().padStart(2, "0")
    );
    const { data: existingPermissions, refetch } = useGetPermissions();
    const { data: user } = useUser();

    const isAdmin = user?.department === "Admin";
    const currentUserPermissions = isAdmin
        ? {
            hr: {
                payroll: true,
            },
        }
        : existingPermissions?.find(p => p.userId === user?.userId);

    const canViewHome =
        isAdmin || currentUserPermissions?.hr?.payroll === true

    const employees = departments.flatMap((dept) =>
        (dept.employees || [])
            .map((emp) => ({
                employeeId: emp.employee_id,
                departmentName: dept.departmentName,
                officeEmail: emp.office_email,
                role: emp.role,
                employeeStatus: emp.employee_status,

                personalDetails: emp.personalDetails || {},

                workInformation: {
                    ...emp.workInformation,
                    firstName: emp.first_name,
                    lastName: emp.last_name,
                    image: emp.image,
                },

                payroll: (emp.payroll || []).map((item) => ({
                    ...item,

                    annualCtc: Number(item.annualCtc || item.annual_ctc || 0),

                    type: item.type || "starting",
                    created_date: item.created_at || item.created_date || null,
                })),
                createdAt: emp.createdAt
            }))
    );

    const filteredEmployees = employees
        .slice()
        .sort((a, b) => {
            const dateA = new Date(a.workInformation?.createdAt || a.createdAt || a.updatedAt || 0);
            const dateB = new Date(b.workInformation?.createdAt || b.createdAt || b.updatedAt || 0);
            return dateB - dateA;
        })
        .filter((emp) => {
            const search = employeeSearch.toLowerCase();

            const matchesSearch =
                emp.workInformation?.firstName?.toLowerCase().includes(search) ||
                emp.workInformation?.lastName?.toLowerCase().includes(search) ||
                String(emp.employeeId || "").toLowerCase().includes(search);

            const matchesDepartment =
                !selectedDepartment ||
                emp.departmentName?.toLowerCase() === selectedDepartment.toLowerCase();

            return matchesSearch && matchesDepartment;
        });

    const selectedEmployee = filteredEmployees.find(
        (emp) => emp.employeeId === selectedEmployeeId
    );

    const filterByMonth = (payrollItem) => {
        const dateVal = payrollItem.monthlyCreated || payrollItem.createdAt;
        if (!dateVal) return false;

        const date = new Date(dateVal);
        const itemYear = date.getFullYear();
        const itemMonth = (date.getMonth() + 1).toString().padStart(2, "0");

        const matchesYear = !selectedYear || itemYear === Number(selectedYear);
        const matchesMonth = !selectedMonth || itemMonth === selectedMonth;
        return matchesYear && matchesMonth;
    };

    const totalSalary = filteredEmployees.reduce((total, emp) => {
        const empTotal = (emp.payroll || [])
            .filter(filterByMonth)
            .reduce((sum, item) => sum + (item.netSalary || 0), 0);

        return total + empTotal;
    }, 0);


    const totalBonus = filteredEmployees.reduce((total, emp) => {
        return total + (emp.payroll || [])
            .filter(filterByMonth)
            .reduce((sum, item) => sum + (item.earnings?.performanceBonus || 0), 0);
    }, 0);

    const totalDeductions = filteredEmployees.reduce((total, emp) => {
        return total + (emp.payroll || [])
            .filter(filterByMonth)
            .reduce((sum, item) => sum + (item.deductions?.totalDeductions || 0), 0);
    }, 0);

    const totalIncrement = filteredEmployees.reduce((total, emp) => {
        return total + (emp.payroll || [])
            .filter(filterByMonth)
            .reduce((sum, item) => sum + (item.earnings?.incrementAmount || 0), 0);
    }, 0);

    if (fetching) {
        return (
            <div className="relative h-[90.7vh] p-2 rs:p-6 overflow-y-auto">
                <SkeletonPage />
            </div>
        );
    }

    if (!canViewHome) {
        return (
            <div>
                <div className="relative h-[90.7vh] p-2 rs:p-6 overflow-hidden z-1">
                    <PermissionPopup />
                </div>
            </div>
        );
    }

    return (
        <div className="relative p-2 rs:p-6 overflow-y-auto space-y-4">
            {selectedEmployeeId && selectedEmployee ? (
                <div className="space-y-4">
                    <div className="flex gap-[10px]">
                        <button className="inline-flex back-btn"
                            onClick={() => setSelectedEmployeeId(null)}
                        >
                            <MoveLeft size={20} />
                        </button>
                        {selectedEmployee && (
                            <div className="rounded flex flex-row gap-[10px] text-sm">
                                <div className="text-[12px] sm:text-[20px] block">
                                    <div>{selectedEmployee.employeeId || "-"} -</div>
                                    <div className="text-[12px]">
                                        {selectedEmployee.departmentName || "-"}
                                    </div>
                                </div>
                                <div className="text-[12px] sm:text-[20px]">
                                    {selectedEmployee.workInformation?.firstName}{" "}
                                    {selectedEmployee.workInformation?.lastName}
                                </div>
                                <div>

                                </div>
                                <div>

                                    <span
                                        className={`px-2 py-1 rounded text-xs ${selectedEmployee.employeeStatus === "Active"
                                            ? "bg-green-100 text-green-700"
                                            : "bg-red-100 text-red-700"
                                            }`}
                                    >
                                        {selectedEmployee.employeeStatus || "-"}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    <PayRollDashboard employee={selectedEmployee} />
                </div>

            ) : (
                <div className="bg-gray-50  rounded-lg space-y-2">
                    <div className="flex flex-col md:flex-row justify-between md:items-end gap-4 bg-white p-2 sm:p-4 rounded-lg shadow">
                        {/* Title */}
                        <h2 className="text-[15px] sm:text-[20px] font-bold text-gray-800">
                            All Employees
                        </h2>

                        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 w-full md:w-auto">
                            <div className="flex flex-wrap sm:flex-nowrap gap-2 w-full sm:w-auto">
                                <select
                                    value={selectedYear}
                                    onChange={(e) => setSelectedYear(e.target.value)}
                                    className="border rounded px-2 py-1 text-xs sm:text-sm sm:px-3 sm:py-2 flex-1 min-w-[100px] sm:w-auto"
                                >
                                    {Array.from({ length: 31 }, (_, i) => 2024 + i).map((year) => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>

                                <select
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                    className="border rounded px-2 py-1 text-xs  sm:text-sm sm:px-3 sm:py-2 flex-1 min-w-[180px] sm:w-auto"
                                >
                                    {Array.from({ length: 12 }, (_, i) => {
                                        const month = (i + 1).toString().padStart(2, "0");
                                        const monthName = new Date(0, i).toLocaleString("default", { month: "long" });
                                        return <option key={month} value={month}>{monthName}</option>;
                                    })}
                                </select>

                                <select
                                    value={selectedDepartment}
                                    onChange={(e) => setSelectedDepartment(e.target.value)}
                                    className="border rounded px-2 py-1 text-xs  sm:text-sm sm:px-3 sm:py-2 flex-1 min-w-[180px] sm:w-auto"
                                >
                                    <option value="">All</option>
                                    <option value="Development">Development</option>
                                    <option value="Management">Management</option>
                                    <option value="HR">HR</option>
                                    <option value="Sales">Sales</option>
                                    <option value="Design">Design</option>
                                </select>
                            </div>

                            <div className="w-full sm:w-64">
                                <input
                                    type="text"
                                    value={employeeSearch}
                                    onChange={(e) => setEmployeeSearch(e.target.value)}
                                    placeholder="Search employees..."
                                    className="input w-full"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 py-[10px] sm:py-[20px]">
                        <div className="group bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 transform">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-[12px] sm:text-sm xl:text-base font-medium opacity-80">Total Salary</p>
                                    <p className="text-[14px] sm:text-lg xl:text-3xl font-bold"> ₹{totalSalary.toLocaleString("en-IN")}</p>
                                </div>
                                <UserRound className="w-6 h-6 md:w-8 md:h-8 xl:w-12 xl:h-12 opacity-80 group-hover:scale-125 transition-transform duration-300" />
                            </div>
                        </div>

                        <div className="group bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 transform ">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-[12px] sm:text-sm xl:text-base font-medium opacity-80">Bonus</p>
                                    <p className="text-[14px] sm:text-lg xl:text-3xl font-bold">
                                        ₹{totalBonus.toLocaleString("en-IN")}
                                    </p>
                                </div>
                                <Volleyball className="w-6 h-6 md:w-8 md:h-8 xl:w-12 xl:h-12 opacity-80 group-hover:scale-125 transition-transform duration-300 " />
                            </div>
                        </div>

                        <div className="group bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 transform">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-[12px] sm:text-sm xl:text-base font-medium opacity-80">Deductions</p>
                                    <p className="text-[14px] sm:text-lg xl:text-3xl font-bold">
                                        ₹{(totalDeductions || 0).toLocaleString("en-IN")}
                                    </p>
                                </div>
                                <Star className="w-6 h-6 md:w-8 md:h-8 xl:w-12 xl:h-12 opacity-80 group-hover:scale-125 transition-transform duration-300" />
                            </div>
                        </div>

                        <div className="group bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 transform">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-[12px] sm:text-sm xl:text-base font-medium opacity-80">Increment</p>
                                    <p className="text-[14px] sm:text-lg xl:text-3xl font-bold">
                                        ₹{(totalIncrement || 0).toLocaleString("en-IN")}
                                    </p>
                                </div>
                                <AlignStartVertical className="w-6 h-6 md:w-8 md:h-8 xl:w-12 xl:h-12 opacity-80 group-hover:scale-125 transition-transform duration-300" />
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm text-left text-gray-700 border rounded-lg overflow-hidden">
                            <thead className="bg-gray-100 text-xs font-semibold text-gray-600">
                                <tr>
                                    <th className="px-2 py-1 sm:px-4 sm:py-2 text-[11px] sm:text-sm">Employee ID</th>
                                    <th className="px-2 py-1 sm:px-4 sm:py-2 text-[11px] sm:text-sm hidden custom:table-cell">Photo</th>
                                    <th className="px-2 py-1 sm:px-4 sm:py-2 text-[11px] sm:text-sm">Full Name</th>
                                    <th className="px-2 py-1 sm:px-4 sm:py-2 text-[11px] sm:text-sm hidden custom:table-cell">Email</th>
                                    <th className="px-2 py-1 sm:px-4 sm:py-2 text-[11px] sm:text-sm hidden zx:table-cell">Department</th>
                                    <th className="px-2 py-1 sm:px-4 sm:py-2 text-[11px] sm:text-sm hidden custom:table-cell">Role</th>
                                    <th className="px-2 py-1 sm:px-4 sm:py-2 text-[11px] sm:text-sm">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {fetching && (
                                    <tr>
                                        <td colSpan={8} className="px-4 py-6">
                                            <SkeletonPage />
                                        </td>
                                    </tr>
                                )}

                                {!fetching && filteredEmployees.length > 0 &&
                                    filteredEmployees.map((emp, index) => {
                                        const workInfo = emp.workInformation || {};
                                        return (
                                            <tr
                                                key={emp.employeeId || index}
                                                onClick={() => setSelectedEmployeeId(emp.employeeId)}
                                                className="bg-white hover:bg-gray-100 cursor-pointer border-t"
                                            >
                                                <td className="px-2 py-1 sm:px-4 sm:py-2 font-medium text-[11px] sm:text-sm">
                                                    {emp.employeeId || "-"}
                                                </td>

                                                <td className="px-2 py-1 sm:px-4 sm:py-2 text-[11px] sm:text-sm hidden custom:table-cell">
                                                    {workInfo.image ? (
                                                        <img
                                                            src={workInfo.image}
                                                            alt="Employee"
                                                            className="w-8 h-8 rounded-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-xs font-semibold">
                                                            {`${workInfo.firstName?.[0] || ""}${workInfo.lastName?.[0] || ""}`}
                                                        </div>
                                                    )}
                                                </td>

                                                <td className="px-2 py-1 sm:px-4 sm:py-2 text-[11px] sm:text-sm">
                                                    {workInfo.firstName || ""} {workInfo.lastName || ""}
                                                </td>

                                                <td className="px-2 py-1 sm:px-4 sm:py-2 truncate text-[11px] sm:text-sm hidden custom:table-cell">
                                                    {emp.officeEmail || "-"}
                                                </td>

                                                <td className="px-2 py-1 sm:px-4 sm:py-2 text-[11px] sm:text-sm hidden zx:table-cell">
                                                    {emp.departmentName || "-"}
                                                </td>

                                                <td className="px-2 py-1 sm:px-4 sm:py-2 text-[11px] sm:text-sm hidden custom:table-cell">
                                                    {emp.role || "-"}
                                                </td>

                                                <td className="px-2 py-1 sm:px-4 sm:py-2 text-[11px] sm:text-sm">
                                                    <span
                                                        className={`px-2 py-1 rounded text-xs ${emp.employeeStatus === "Active"
                                                            ? "bg-green-100 text-green-700"
                                                            : emp.employeeStatus === "Terminated"
                                                                ? "bg-red-100 text-red-700"
                                                                : emp.employeeStatus === "Resigned"
                                                                    ? "bg-orange-100 text-orange-700"
                                                                    : emp.employeeStatus === "Trainee"
                                                                        ? "bg-blue-100 text-blue-700"
                                                                        : "bg-gray-100 text-gray-600"
                                                            }`}
                                                    >
                                                        {emp.employeeStatus || "-"}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}

                                {!fetching && filteredEmployees.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="text-center py-4 text-sm text-gray-400">
                                            No employees in this department
                                        </td>
                                    </tr>
                                )}
                            </tbody>

                        </table>
                    </div>
                </div>
            )}
        </div>
    )
}
